import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay, useAlert } from '../../components';
import { getUserProducts, claimDailyIncome, getProducts } from '../../services/firestoreService';
import { formatCurrency, canClaimToday } from '../../utils/helpers';
import './MyProductsScreen.css';

export const MyProductsScreen = () => {
    const navigate = useNavigate();
    const { refreshUserData } = useAuth();
    const alert = useAlert();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(null);
    const [claimingOverlay, setClaimingOverlay] = useState(false);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const [userProducts, allProductsData] = await Promise.all([
                getUserProducts(),
                getProducts()
            ]);

            const productMap = new Map();
            allProductsData.forEach(product => {
                productMap.set(product.id, product);
            });

            const enrichedProducts = userProducts.map(userProduct => {
                const originalProduct = productMap.get(userProduct.productId);
                return {
                    ...userProduct,
                    imageUrl: originalProduct?.imageUrl || null
                };
            });

            setProducts(enrichedProducts);
        } catch (error) {
            alert.showError('Error', 'Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleClaimIncome = async (product) => {
        if (!canClaimToday(product.lastClaimDate, product.purchaseDate)) {
            alert.showInfo('Cannot Claim Yet', 'Daily income can be claimed once every 24 hours after midnight!');
            return;
        }

        setClaiming(product.id);
        setClaimingOverlay(true);
        try {
            await claimDailyIncome(product);
            await refreshUserData();
            await loadProducts();
            setClaimingOverlay(false);
            alert.showSuccess('Success', `Successfully claimed ${formatCurrency(product.dailyIncome)}!`);
        } catch (error) {
            setClaimingOverlay(false);
            alert.showError('Error', error.message || 'Failed to claim income');
        } finally {
            setClaiming(null);
        }
    };

    const getDefaultImage = (index) => {
        const images = [
            'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=400&fit=crop',
        ];
        return images[index % images.length];
    };

    const calculateDaysPassed = (purchaseDate, totalClaimed, dailyIncome) => {
        if (!purchaseDate) return 0;

        // Calculate days from time elapsed (Inclusive: Day 1 starts immediately)
        const start = purchaseDate?.toDate ? purchaseDate.toDate() : new Date(purchaseDate);
        const now = new Date();

        const diffInMs = now.getTime() - start.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;

        // Also factor in total claimed as a backup (if they claimed 2 times, they must be at least on day 2)
        const claimCount = Math.floor((totalClaimed || 0) / dailyIncome);

        return Math.max(diffInDays, claimCount);
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading active assets..." />;
    }

    return (
        <div className="active-assets-screen">
            {/* Non-dismissable claiming overlay */}
            {claimingOverlay && (
                <div className="claiming-overlay">
                    <div className="claiming-dialog">
                        <div className="claiming-shield-ring">
                            <div className="claiming-shield-inner">
                                <span className="material-icons claiming-shield-icon">verified_user</span>
                            </div>
                        </div>
                        <p className="claiming-title">PRODUCT CLAIMING</p>
                        <div className="claiming-warning-row">
                            <span className="material-icons claiming-warning-icon">info</span>
                            <span className="claiming-warning-text">PLEASE DON'T GO BACK</span>
                        </div>
                        <div className="claiming-progress-bar">
                            <div className="claiming-progress-fill"></div>
                        </div>
                        <p className="claiming-sync-text">SYNCING...</p>
                    </div>
                </div>
            )}
            <div className="assets-header">
                <button className="asset-back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="asset-screen-title">ACTIVE ASSETS</h1>
            </div>

            <div className="assets-content-area">
                {products.length > 0 ? (
                    <div className="assets-grid">
                        {products.map((product, index) => {
                            const daysPassed = calculateDaysPassed(product.purchaseDate, product.totalClaimed, product.dailyIncome);
                            const canClaim = canClaimToday(product.lastClaimDate, product.purchaseDate);
                            const isExpired = daysPassed >= product.validity;

                            return (
                                <div key={product.id} className="asset-card-modern">
                                    <div className="asset-card-top">
                                        <div className="asset-image-box">
                                            <img
                                                src={product.imageUrl || getDefaultImage(index)}
                                                alt={product.productName}
                                            />
                                        </div>
                                        <div className="asset-main-info">
                                            <div className="asset-title-row">
                                                <h3 className="asset-name-text">{product.productName}</h3>
                                                <span className="asset-running-badge">RUNNING</span>
                                            </div>
                                            <span className="asset-id-text">ID: NXT-{product.id.substring(0, 6).toUpperCase()}</span>
                                            <div className="asset-tags-row">
                                                <span className="asset-version-tag">V.1.0</span>
                                                <span className="asset-plan-tag">{product.validity} DAYS PLAN</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="asset-stats-row">
                                        <div className="asset-stat-box">
                                            <span className="stat-label-tiny">DAILY INCOME</span>
                                            <span className="stat-value-blue">{formatCurrency(product.dailyIncome)}</span>
                                        </div>
                                        <div className="asset-stat-box">
                                            <span className="stat-label-tiny">TOTAL PROFIT</span>
                                            <span className="stat-value-green">{formatCurrency(product.totalClaimed || 0)}</span>
                                        </div>
                                    </div>

                                    <div className="asset-progress-section">
                                        <div className="progress-labels-row">
                                            <span className="p-label-left">TIME PROGRESS</span>
                                            <span className="p-label-right">{Math.min(daysPassed, product.validity)} / {product.validity} DAYS</span>
                                        </div>
                                        <div className="modern-progress-track">
                                            <div
                                                className="modern-progress-thumb"
                                                style={{ width: `${Math.min(100, (daysPassed / product.validity) * 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <button
                                        className="claim-profit-btn"
                                        onClick={() => handleClaimIncome(product)}
                                        disabled={!canClaim || isExpired || claiming === product.id}
                                    >
                                        <span className="material-icons">bolt</span>
                                        {claiming === product.id
                                            ? 'CLAIMING...'
                                            : isExpired
                                                ? 'EXPIRED'
                                                : !canClaim && product.lastClaimDate
                                                    ? 'CLAIMED TODAY'
                                                    : !canClaim
                                                        ? 'AVAILABLE TOMORROW'
                                                        : 'CLAIM DAILY PROFIT'
                                        }
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="no-assets-box">
                        <div className="empty-asset-icon">
                            <span className="material-icons">inventory_2</span>
                        </div>
                        <p className="no-assets-text">NO MORE ACTIVE ASSETS</p>
                    </div>
                )}
            </div>

            <p className="claim-helper-footer">DAILY INCOME CAN BE CLAIMED ONCE EVERY 24 HOURS</p>
        </div>
    );
};

export default MyProductsScreen;
